import 'custom-event-polyfill';
import { isEqual } from 'lodash';
import { BroadcastChannel } from 'broadcast-channel';

import { vrmConfig } from '@/config';

/**
 * @typedef {Object} Banner
 * @property {boolean} showGlobalMessage
 * @property {string} globalMessage
 * @property {string} globalMessageType
 */
window.__banner = {};

try {
    window.__banner = JSON.parse(localStorage.getItem('vrmBanner') || '{}');
} catch (e) {
    console.error('Could not parse banner from localStorage', e);
}

const CURRENT_BANNER_MESSAGE = 'current-banner';

const setupChannel = () => {
    const bannerBroadcastChannel = new BroadcastChannel('latest-banner', {
        idb: {
            onclose: () => {
                bannerBroadcastChannel.close();
                setupChannel();
            },
        },
    });

    // listen for other tabs versions
    bannerBroadcastChannel.onmessage = event => {
        // check banner js if it has been updated
        if (event && event.type === CURRENT_BANNER_MESSAGE) {
            if (!isEqual(window.__banner, event.banner)) {
                // some other tab has a newer version
                performUpdateCheck();
            }
        }
    };

    const broadcastBanner = banner => {
        bannerBroadcastChannel.postMessage({
            type: CURRENT_BANNER_MESSAGE,
            banner,
        });
    };

    return { broadcastBanner };
};

const channel = setupChannel();

const UPDATE_CHECK_INTERVAL = 60 * 1000;
export const BANNER_UPDATED_EVENT = 'vrmBannerUpdated';

import axios from 'axios';
const endpoint = axios.create({
    baseURL: window.location.origin,
});

const logger = console.info.bind(console, '[banner-detector]');

/**
 * Detect if there is an update of the banner
 * If the server has a newer banner of the global message this resolves to true.
 * Rejects in case of network and server errors
 * @returns {Promise<object>}
 */
async function checkUpdateAvailable() {
    let serverBanner = {};
    try {
        const { data } = await endpoint.get(
            `/config/banner.json?cachebust=${new Date().getTime()}`,
            {
                responseType: 'json',
            },
        );
        serverBanner = data;
    } catch (e) {
        console.error("Couldn't fetch banner", e);
        serverBanner = {};
    }
    return !isEqual(window.__banner, serverBanner) && serverBanner;
}

async function performUpdateCheck() {
    logger('Performing banner update check');
    let serverBanner = false;
    try {
        serverBanner = await checkUpdateAvailable();
        if (serverBanner) {
            logger('New banner available');
            localStorage.setItem('vrmBanner', JSON.stringify(serverBanner));
            window.__banner = serverBanner;
            channel.broadcastBanner(serverBanner);
            window.dispatchEvent(new CustomEvent(BANNER_UPDATED_EVENT));
            sessionStorage.setItem('globalMessageDismissed', '');
        }
    } finally {
        scheduleUpdateCheck();
    }
}

let scheduledCheckTimeout = null;

function scheduleUpdateCheck() {
    clearTimeout(scheduledCheckTimeout);
    scheduledCheckTimeout = setTimeout(performUpdateCheck, UPDATE_CHECK_INTERVAL);
}

if (!vrmConfig.debug) {
    logger('Activating banner detector, current app banner is', window.__banner);
    performUpdateCheck();
    scheduleUpdateCheck();
} else {
    logger('Auto reload disabled in debug mode');
}
